import React, { useEffect, useState } from "react";
import { ChartContainer } from "../view/ChartContainer";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CustomTooltip } from "./components/CustomTooltip";
import { transformBarChartData, extractDatasetConfigs } from "./utils/chartDataTransformers";
import { DEVICE_COLORS_12 } from "./constants/chartColors";
import * as statisticService from "./statisticService";
import { AuditScope } from "./chartDynamicLabels";
import { InteractiveLegend } from "./components/InteractiveLegend";
import DashboardChartSkeleton from "./components/DashboardChartSkeleton";

function ViolationsByDeviceChart({ fromDate, toDate, scope = AuditScope.BY_DAY }) {

    const [rechartsData, setRechartsData] = useState([]);
    const [datasetConfigs, setDatasetConfigs] = useState([]);
    const [hiddenSeries, setHiddenSeries] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const vData = await statisticService.getViolationsByDevice(scope);
            if (vData && vData.labels && vData.datasets) {
                // Transform data for Recharts
                const transformed = transformBarChartData(vData);
                setRechartsData(transformed);
                // Extract dataset configurations for rendering bars
                const configs = extractDatasetConfigs(vData);
                setDatasetConfigs(configs);
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (scope !== AuditScope.CUSTOM)
            fetchData();
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [scope])

    const fetchDataCustom = async () => {
        setIsLoading(true);
        try {
            const vData = await statisticService.getViolationsByDeviceCustom(fromDate, toDate);
            if (vData && vData.labels && vData.datasets) {

                // Transform data for Recharts
                const transformed = transformBarChartData(vData);
                setRechartsData(transformed);
                // Extract dataset configurations for rendering bars
                const configs = extractDatasetConfigs(vData);
                setDatasetConfigs(configs);
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (scope !== AuditScope.CUSTOM) {
            return;
        }

        const fromNotToday = isDateDifferentThanToday(fromDate);
        const toNotToday = isDateDifferentThanToday(toDate);
        checkCustomDatesSelected(fromNotToday, toNotToday);
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [fromDate, toDate, scope])

    const checkCustomDatesSelected = (isFromDate, isToDate) => {
        if (isFromDate === true || isToDate === true) {
            fetchDataCustom();
        }
    }

    const isDateDifferentThanToday = (dateToCheck) => {
        if (!dateToCheck) {
            return false;
        }
        const today = new Date();
        const isSameDay =
            dateToCheck.getFullYear() === today.getFullYear() &&
            dateToCheck.getMonth() === today.getMonth() &&
            dateToCheck.getDate() === today.getDate();

        return !isSameDay;
    };

    // Handle legend click to toggle series visibility
    const handleToggleSeries = (dataKey) => {
        setHiddenSeries(prev => ({
            ...prev,
            [dataKey]: !prev[dataKey]
        }));
    };

    if (isLoading) {
        return <DashboardChartSkeleton title="Violation by Device" subtitle="Violations" />;
    }

    return (
        <ChartContainer noHeader={true} className="oct_chart-card-v2">
            <div className="oct_chart-header-v2">
                <p className="oct_chart-subtitle-v2">Violations</p>
                <h3 className="oct_chart-title-v2">Violation by Device</h3>
            </div>
            <div className="oct_interactive-legend-wrapper-v2">
                {datasetConfigs.length > 0 && (
                    <InteractiveLegend
                        payload={datasetConfigs.map((dataset, index) => ({
                            value: dataset.name,
                            color: DEVICE_COLORS_12[index % DEVICE_COLORS_12.length],
                            dataKey: dataset.key
                        }))}
                        hiddenSeries={hiddenSeries}
                        onToggleSeries={handleToggleSeries}
                    />
                )}
            </div>
            <div className="oct_recharts-wrapper-v2 oct_chart-no-interaction">
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 10, height: 10 }}>
                    <BarChart
                        data={rechartsData}
                        barSize={15}
                        barCategoryGap="15%"
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                            dataKey="label"
                            stroke="#94a3b8"
                            tick={{ fontSize: 12 }}
                        />
                        <YAxis
                            stroke="#94a3b8"
                            tick={{ fontSize: 12 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        {datasetConfigs.map((dataset, index) => (
                            <Bar
                                key={dataset.key}
                                dataKey={dataset.key}
                                fill={DEVICE_COLORS_12[index % DEVICE_COLORS_12.length]}
                                radius={[4, 4, 0, 0]}
                                name={dataset.name}
                                hide={hiddenSeries[dataset.key]}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </ChartContainer>
    )
}

export default ViolationsByDeviceChart;
